/**
 * Path chooser for scenario creation. Two real paths (the closed-beta email
 * wall is gone — the manual builder + negotiation sheet is the free tool):
 *  - Manual (FREE): enter codes/quantities, see national averages, print a
 *    negotiation sheet with the cheapest nearby published prices — your comps.
 *  - Analyst ($1.99): the /price-plan funnel — deep verification session +
 *    per-procedure hospital optimization.
 * Plus template: start from an existing catalog scenario.
 */
import { startTransition } from 'react';
import { Hammer, ArrowRight, Sparkles, Printer, FileSearch, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

interface ScenarioPathChooserProps {
  onChoose: (path: 'manual' | 'ai' | 'template') => void;
  /** Close the host dialog before navigating away to /price-plan. */
  onNavigateAway?: () => void;
}

export function ScenarioPathChooser({ onChoose, onNavigateAway }: ScenarioPathChooserProps) {
  const brand = useBrand();
  const isAhp = brand.design === 'civic';
  const { isDark } = useTheme();
  const ahpDark = isAhp && isDark;
  const navigate = useNavigate();
  const { t } = useTranslation(['scenarios', 'common']);

  const cardCls = `rounded-xl border-2 p-5 text-left w-full transition-all hover:-translate-y-0.5 ${
    ahpDark ? 'border-civic-gold/25 bg-civic-card-dark hover:border-civic-gold'
      : isAhp ? 'border-gray-200 bg-white hover:border-civic-navy'
      : 'border-white/10 bg-white/5 hover:border-matrix-green/60'
  }`;
  const titleCls = ahpDark ? 'text-civic-warm' : isAhp ? 'text-gray-900' : 'text-foreground';

  return (
    <div className="space-y-4 py-4">
      <div className="text-center mb-2">
        <h2 className={`text-xl font-bold ${titleCls}`}>
          {t('builder.chooserTitle', { defaultValue: 'Build a Scenario' })}
        </h2>
        <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
          {t('builder.chooserSub', { defaultValue: 'Price out your exact situation — free by hand, or let a deep analysis session do it for you.' })}
        </p>
      </div>

      {/* FREE manual path */}
      <button onClick={() => onChoose('manual')} className={cardCls}>
        <div className="flex items-start gap-3">
          <div className={`p-2.5 rounded-lg shrink-0 ${isAhp ? 'bg-civic-navy' : 'bg-matrix-green/20'}`}>
            <Hammer className={`w-5 h-5 ${isAhp ? 'text-civic-gold' : 'text-matrix-green'}`} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`font-bold ${titleCls}`}>{t('builder.manualTitle', { defaultValue: 'Build it yourself' })}</span>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isAhp ? 'bg-green-100 text-green-800' : 'bg-matrix-green/20 text-matrix-green'}`}>
                {t('builder.free', { defaultValue: 'FREE' })}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              {t('builder.manualDesc', { defaultValue: 'Enter procedure codes and quantities (or search by name), see the national average sticker price per line, and print a negotiation sheet with the cheapest published prices near you — your comps for the call.' })}
            </p>
            <div className="flex items-center gap-3 mt-2 text-[11px] text-muted-foreground">
              <span className="inline-flex items-center gap-1"><FileSearch className="w-3 h-3" /> {t('builder.codeLookup', { defaultValue: 'Code lookup' })}</span>
              <span className="inline-flex items-center gap-1"><Printer className="w-3 h-3" /> {t('builder.negotiationSheet', { defaultValue: 'Negotiation sheet' })}</span>
            </div>
          </div>
          <ArrowRight className="w-4 h-4 shrink-0 mt-1 text-muted-foreground" />
        </div>
      </button>

      {/* $1.99 analyst path -> /price-plan funnel. The route is lazy-loaded:
          navigating synchronously from the click suspends the route boundary
          mid-update (React #426 -> error boundary), so close the dialog and
          navigate inside startTransition. */}
      <button
        disabled
        aria-disabled="true"
        title="Coming soon"
        className={`${cardCls} opacity-45 cursor-not-allowed pointer-events-none`}
      >
        <div className="flex items-start gap-3">
          <div className={`p-2.5 rounded-lg shrink-0 ${isAhp ? 'bg-civic-red' : 'bg-amber-500/20'}`}>
            <Sparkles className={`w-5 h-5 ${isAhp ? 'text-white' : 'text-amber-400'}`} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`font-bold ${titleCls}`}>{t('builder.analystTitle', { defaultValue: 'Let our analyst build it' })}</span>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isAhp ? 'bg-gray-500 text-white' : 'bg-white/15 text-muted-foreground'}`}>
                COMING SOON
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              {t('builder.analystDesc', { defaultValue: 'Describe your situation in plain words. A deep analysis session verifies every code, setting, and drug quantity against the national database, prices your combination at every nearby hospital, and finds your cheapest arrangement.' })}
            </p>
            <div className="flex items-center gap-1 mt-2 text-[11px] text-muted-foreground">
              <ShieldCheck className="w-3 h-3" /> {t('builder.analystNote', { defaultValue: 'Every code verified · ready in ~15 minutes' })}
            </div>
          </div>
          <ArrowRight className="w-4 h-4 shrink-0 mt-1 text-muted-foreground" />
        </div>
      </button>

      {/* Template path */}
      <div className="text-center pt-1">
        <button
          onClick={() => onChoose('template')}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            isAhp ? 'bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-white/10 dark:hover:bg-white/20 dark:text-gray-200'
              : 'bg-white/10 hover:bg-white/20 text-foreground'
          }`}
        >
          {t('builder.startFromTemplate', { defaultValue: 'Or start from one of 11,000+ existing scenarios' })}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
